import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, History, Loader2, Pencil, Pin, PinOff, Plus, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  api,
  type LearningMemory,
  type LearningMemoryView,
  type LearningRunDiagnostics,
  type MemoryCapacityUsage,
  type ShippedSkillRecommendation,
  type ShippedSkillRecommendationInbox,
  type ShippedSkillRecommendationStatus,
  type ShippedSkillRecommendationSource,
  type TenantSkillLearning,
  type TenantSkillLearningView,
  type TenantSkillRevision,
  type TrainingMemoryCategory,
} from '@/services/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

type LearningTab = 'mine' | 'tenant' | 'skills' | 'recommendations';
type PendingAction = {
  kind: 'archive' | 'rollback' | 'resolve';
  memory: LearningMemory;
} | null;
type PendingSkillAction = {
  kind: 'archive' | 'rollback';
  skill: TenantSkillLearning;
  revision?: TenantSkillRevision;
} | null;
type PendingRecommendationAction = {
  recommendation: ShippedSkillRecommendation;
  status: ShippedSkillRecommendationStatus;
} | null;

function CapacityCard({
  label,
  usage,
}: {
  label: string;
  usage: { usedEntries: number; maxEntries: number; usedChars: number; maxChars: number; reviewRequired: boolean };
}) {
  const percent = Math.min(100, Math.round((usage.usedEntries / usage.maxEntries) * 100));
  return (
    <div className="rounded-lg border border-surface-200 p-3 dark:border-surface-700">
      <div className="flex items-center justify-between text-xs text-surface-500 dark:text-surface-400">
        <span>{label}</span>
        <span>{usage.usedEntries} / {usage.maxEntries}</span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-100 dark:bg-surface-800">
        <div className="h-full rounded-full bg-primary-500" style={{ width: `${percent}%` }} />
      </div>
      <div className="mt-1 text-[11px] text-surface-400">
        {usage.usedChars.toLocaleString()} / {usage.maxChars.toLocaleString()} characters
      </div>
      {usage.reviewRequired && (
        <div className="mt-2 text-[11px] font-medium text-amber-600 dark:text-amber-300">
          Dry-run consolidation review required; no history was deleted.
        </div>
      )}
    </div>
  );
}

function CapacityReview({
  usage,
  onResolve,
}: {
  usage: MemoryCapacityUsage;
  onResolve: () => Promise<void>;
}) {
  const review = usage.consolidationReview;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
      <div className="font-medium">Capacity dry-run and local backup</div>
      {review && (
        <div className="mt-1 space-y-1 text-amber-800 dark:text-amber-200">
          <div>Rejected change needs {review.requiredEntryDelta} additional entr{review.requiredEntryDelta === 1 ? 'y' : 'ies'} and {review.requiredCharDelta.toLocaleString()} characters.</div>
          <div>Projected usage: {review.projectedEntries} entries / {review.projectedChars.toLocaleString()} characters.</div>
          <div>Local backup snapshot: {review.backupEntryCount} entr{review.backupEntryCount === 1 ? 'y' : 'ies'}.</div>
        </div>
      )}
      <p className="mt-2 text-amber-700 dark:text-amber-300">
        Archive or consolidate enough active Memory first. Completion is rejected until the attempted change would fit.
      </p>
      <Button size="sm" variant="outline" className="mt-2" onClick={() => void onResolve()}>
        Mark capacity review complete
      </Button>
    </div>
  );
}

function statusClass(status: LearningMemory['status']): string {
  if (status === 'active') return 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300';
  if (status === 'proposed') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  return 'bg-surface-100 text-surface-600 dark:bg-surface-700 dark:text-surface-300';
}

export function LearningPanel() {
  const [view, setView] = useState<LearningMemoryView | null>(null);
  const [skillView, setSkillView] = useState<TenantSkillLearningView | null>(null);
  const [recommendationView, setRecommendationView] = useState<ShippedSkillRecommendationInbox | null>(null);
  const [diagnostics, setDiagnostics] = useState<LearningRunDiagnostics | null>(null);
  const [tab, setTab] = useState<LearningTab>('mine');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [pendingSkillAction, setPendingSkillAction] = useState<PendingSkillAction>(null);
  const [pendingRecommendationAction, setPendingRecommendationAction] = useState<PendingRecommendationAction>(null);
  const [recommendationFilter, setRecommendationFilter] = useState<'all' | ShippedSkillRecommendationStatus>('all');
  const [recommendationStatuses, setRecommendationStatuses] = useState<Record<string, ShippedSkillRecommendationStatus>>({});
  const [recommendationNotes, setRecommendationNotes] = useState<Record<string, string>>({});
  const [recommendationSources, setRecommendationSources] = useState<Record<string, ShippedSkillRecommendationSource>>({});
  const [saving, setSaving] = useState(false);
  const [memoryEditor, setMemoryEditor] = useState<{
    memory: LearningMemory | null;
    memoryKey: string;
    category: TrainingMemoryCategory;
    title: string;
    content: string;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const memory = await api.learning.getMemory();
      const [skills, recommendations, learningDiagnostics] = await Promise.all([
        api.learning.getTenantSkills(),
        memory.permissions.viewRecommendations
          ? api.learning.getRecommendations()
          : Promise.resolve(null),
        memory.permissions.viewRecommendations
          ? api.learning.getDiagnostics()
          : Promise.resolve(null),
      ]);
      setView(memory);
      setSkillView(skills);
      setRecommendationView(recommendations);
      setDiagnostics(learningDiagnostics);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load Learning');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const memories = useMemo(
    () => tab === 'mine' ? view?.myMemory ?? [] : view?.tenantMemory ?? [],
    [tab, view]
  );

  const performPendingAction = async () => {
    if (!pendingAction) return;
    setSaving(true);
    try {
      if (pendingAction.kind === 'archive') {
        await api.learning.setMemoryLifecycle(
          pendingAction.memory.id,
          'archived',
          'Archived from Learning settings'
        );
      } else if (pendingAction.kind === 'rollback') {
        await api.learning.rollbackMemory(pendingAction.memory.id);
      } else {
        await api.learning.resolveMemoryConflict(pendingAction.memory.id);
      }
      toast.success(
        pendingAction.kind === 'archive'
          ? 'Memory archived'
          : pendingAction.kind === 'rollback'
            ? 'Memory revision restored'
            : 'Memory conflict resolved'
      );
      setPendingAction(null);
      await load();
    } catch (actionError) {
      toast.error(actionError instanceof Error ? actionError.message : 'Memory update failed');
    } finally {
      setSaving(false);
    }
  };

  const performPendingSkillAction = async () => {
    if (!pendingSkillAction) return;
    setSaving(true);
    try {
      if (pendingSkillAction.kind === 'archive') {
        await api.learning.setTenantSkillLifecycle(pendingSkillAction.skill.id, 'archived');
        toast.success('Tenant Skill archived');
      } else if (pendingSkillAction.revision) {
        await api.learning.rollbackTenantSkill(
          pendingSkillAction.skill.id,
          pendingSkillAction.revision.id
        );
        toast.success('Tenant Skill revision restored');
      }
      setPendingSkillAction(null);
      await load();
    } catch (actionError) {
      toast.error(actionError instanceof Error ? actionError.message : 'Tenant Skill update failed');
    } finally {
      setSaving(false);
    }
  };

  const performPendingRecommendationAction = async () => {
    if (!pendingRecommendationAction) return;
    setSaving(true);
    try {
      const { recommendation, status } = pendingRecommendationAction;
      await api.learning.setRecommendationStatus(
        recommendation.id,
        status,
        recommendationNotes[recommendation.id]?.trim() || undefined
      );
      toast.success('Recommendation status updated locally');
      setPendingRecommendationAction(null);
      await load();
    } catch (actionError) {
      toast.error(actionError instanceof Error ? actionError.message : 'Recommendation update failed');
    } finally {
      setSaving(false);
    }
  };

  const saveMemoryEditor = async () => {
    if (!memoryEditor || (tab !== 'mine' && tab !== 'tenant')) return;
    setSaving(true);
    try {
      if (memoryEditor.memory) {
        await api.learning.correctMemory(memoryEditor.memory.id, {
          title: memoryEditor.title,
          content: memoryEditor.content,
        });
        toast.success('Memory correction saved as a new revision');
      } else {
        await api.learning.createMemory({
          scope: tab === 'tenant' ? 'tenant' : 'user',
          memoryKey: memoryEditor.memoryKey,
          category: memoryEditor.category,
          title: memoryEditor.title,
          content: memoryEditor.content,
        });
        toast.success('Memory Entry created');
      }
      setMemoryEditor(null);
      await load();
    } catch (saveError) {
      toast.error(saveError instanceof Error ? saveError.message : 'Memory Entry could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const activeByKey = useMemo(() => new Set(
    memories.filter((memory) => memory.status === 'active' && !memory.conflictGroupId)
      .map((memory) => memory.memoryKey)
  ), [memories]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium text-surface-900 dark:text-white">Learning</h3>
          <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
            Inspect active context and the attributed, reversible history behind it.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {view && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <CapacityCard label={view.permissions.viewAllUserMemory ? 'Current support user capacity' : 'My Memory capacity'} usage={view.capacity.user} />
            {view.capacity.user.reviewRequired && (
              <CapacityReview
                usage={view.capacity.user}
                onResolve={async () => {
                  await api.learning.resolveMemoryConsolidationReview('user');
                  await load();
                }}
              />
            )}
          </div>
          <div className="space-y-2">
            <CapacityCard label="Tenant Memory capacity" usage={view.capacity.tenant} />
            {view.capacity.tenant.reviewRequired && view.permissions.manageTenantMemory && (
              <CapacityReview
                usage={view.capacity.tenant}
                onResolve={async () => {
                  await api.learning.resolveMemoryConsolidationReview('tenant');
                  await load();
                }}
              />
            )}
          </div>
        </div>
      )}

      <div className="flex gap-1 rounded-lg bg-surface-100 p-1 dark:bg-surface-800">
        {([
          ['mine', view?.permissions.viewAllUserMemory ? 'User Memory' : 'My Memory'],
          ['tenant', 'Tenant Memory'],
          ['skills', 'Tenant Skills'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              tab === id
                ? 'bg-white text-surface-900 shadow-sm dark:bg-surface-700 dark:text-white'
                : 'text-surface-500 hover:text-surface-800 dark:text-surface-400 dark:hover:text-surface-200'
            )}
          >
            {label}
          </button>
        ))}
        {view?.permissions.viewRecommendations && (
          <button
            onClick={() => setTab('recommendations')}
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              tab === 'recommendations'
                ? 'bg-white text-surface-900 shadow-sm dark:bg-surface-700 dark:text-white'
                : 'text-surface-500 hover:text-surface-800 dark:text-surface-400 dark:hover:text-surface-200'
            )}
          >
            Recommendations{recommendationView?.newCount ? ` (${recommendationView.newCount})` : ''}
          </button>
        )}
      </div>

      {(tab === 'mine' || (tab === 'tenant' && view?.permissions.manageTenantMemory)) && (
        <div className="space-y-3">
          {!memoryEditor ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setMemoryEditor({
                memory: null,
                memoryKey: '',
                category: tab === 'tenant' ? 'workflow' : 'preference',
                title: '',
                content: '',
              })}
            >
              <Plus className="mr-2 h-4 w-4" /> Add {tab === 'tenant' ? 'Tenant' : 'User'} Memory
            </Button>
          ) : (
            <div className="rounded-lg border border-surface-200 p-4 dark:border-surface-700">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-surface-900 dark:text-surface-100">
                  {memoryEditor.memory ? 'Correct Memory Entry' : 'Add Memory Entry'}
                </h4>
                <Button size="sm" variant="ghost" aria-label="Close memory editor" onClick={() => setMemoryEditor(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="text-xs text-surface-500">
                  Stable key
                  <input
                    className="mt-1 w-full rounded-md border border-surface-300 bg-transparent px-3 py-2 text-sm dark:border-surface-600"
                    value={memoryEditor.memoryKey}
                    disabled={Boolean(memoryEditor.memory)}
                    onChange={(event) => setMemoryEditor({ ...memoryEditor, memoryKey: event.target.value })}
                    placeholder="reports.summary-style"
                  />
                </label>
                <label className="text-xs text-surface-500">
                  Category
                  <select
                    className="mt-1 w-full rounded-md border border-surface-300 bg-transparent px-3 py-2 text-sm dark:border-surface-600"
                    value={memoryEditor.category}
                    disabled={Boolean(memoryEditor.memory)}
                    onChange={(event) => setMemoryEditor({ ...memoryEditor, category: event.target.value as TrainingMemoryCategory })}
                  >
                    {(tab === 'tenant'
                      ? ['workflow', 'policy', 'definition', 'fact', 'decision', 'note']
                      : ['preference', 'workflow', 'definition', 'fact', 'decision', 'note']
                    ).map((category) => <option key={category} value={category}>{category}</option>)}
                  </select>
                </label>
              </div>
              <label className="mt-3 block text-xs text-surface-500">
                Title
                <input
                  className="mt-1 w-full rounded-md border border-surface-300 bg-transparent px-3 py-2 text-sm dark:border-surface-600"
                  value={memoryEditor.title}
                  maxLength={120}
                  onChange={(event) => setMemoryEditor({ ...memoryEditor, title: event.target.value })}
                />
              </label>
              <label className="mt-3 block text-xs text-surface-500">
                Content
                <textarea
                  className="mt-1 min-h-28 w-full rounded-md border border-surface-300 bg-transparent px-3 py-2 text-sm dark:border-surface-600"
                  value={memoryEditor.content}
                  maxLength={2000}
                  onChange={(event) => setMemoryEditor({ ...memoryEditor, content: event.target.value })}
                />
              </label>
              <div className="mt-3 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setMemoryEditor(null)}>Cancel</Button>
                <Button onClick={() => void saveMemoryEditor()} disabled={saving}>Save revision</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'recommendations' ? (
        !recommendationView ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary-500" /></div>
        ) : (
          <div className="space-y-4">
            {diagnostics && (
              <details className="rounded-lg border border-surface-200 p-4 dark:border-surface-700">
                <summary className="cursor-pointer text-sm font-medium text-surface-800 dark:text-surface-100">
                  Learning Run diagnostics · {diagnostics.counts.pending} pending · {diagnostics.counts.running} running · {diagnostics.counts.failed} failed
                </summary>
                <div className="mt-3 max-h-64 space-y-2 overflow-auto">
                  {diagnostics.runs.slice(0, 50).map((run) => (
                    <div key={run.id} className="flex flex-wrap justify-between gap-2 rounded-md bg-surface-50 p-2 text-xs text-surface-500 dark:bg-surface-800 dark:text-surface-400">
                      <span>{run.id} · {run.provider}{run.reviewerProvider && run.reviewerProvider !== run.provider ? ` → ${run.reviewerProvider}` : ''}</span>
                      <span>{run.status}{run.errorCode ? ` · ${run.errorCode}` : ''} · attempt {run.attemptCount}/{run.maxAttempts}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-surface-500 dark:text-surface-400">
                Local support review only. Status changes do not modify shipped Skills or leave this instance.
              </p>
              <select
                value={recommendationFilter}
                onChange={(event) => setRecommendationFilter(event.target.value as typeof recommendationFilter)}
                className="rounded-md border border-surface-300 bg-white px-3 py-2 text-sm dark:border-surface-600 dark:bg-surface-800"
                aria-label="Filter recommendation status"
              >
                <option value="all">All statuses</option>
                <option value="new">New</option>
                <option value="watching">Watching</option>
                <option value="accepted_for_dev">Accepted for development</option>
                <option value="dismissed_tenant_specific">Dismissed: tenant-specific</option>
                <option value="dismissed_incorrect">Dismissed: incorrect</option>
                <option value="resolved_by_release">Resolved by release</option>
              </select>
            </div>
            {recommendationView.recommendations
              .filter((recommendation) => recommendationFilter === 'all' || recommendation.status === recommendationFilter)
              .map((recommendation) => (
                <details key={recommendation.id} className="rounded-lg border border-surface-200 p-4 dark:border-surface-700">
                  <summary className="cursor-pointer list-none">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="font-medium text-surface-900 dark:text-surface-100">{recommendation.skillName}</h4>
                          <span className="rounded-full bg-surface-100 px-2 py-0.5 text-xs text-surface-600 dark:bg-surface-700 dark:text-surface-300">{recommendation.status.replace(/_/g, ' ')}</span>
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">{recommendation.category.replace(/_/g, ' ')}</span>
                        </div>
                        <p className="mt-2 text-sm text-surface-600 dark:text-surface-300">{recommendation.summary}</p>
                      </div>
                      <div className="text-right text-xs text-surface-400">
                        <div>{recommendation.ownerKind}{recommendation.connectorKey ? ` · ${recommendation.connectorKey}` : ''}</div>
                        <div>v{recommendation.shippedVersion} · {recommendation.evidenceCount} evidence</div>
                        <div>{new Date(recommendation.mostRecentEvidenceAt).toLocaleString()}</div>
                      </div>
                    </div>
                  </summary>
                  <div className="mt-4 grid gap-4 border-t border-surface-200 pt-4 dark:border-surface-700 lg:grid-cols-2">
                    <div>
                      <h5 className="text-xs font-semibold uppercase tracking-wide text-surface-500">Shipped excerpt</h5>
                      <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-surface-50 p-3 text-xs text-surface-600 dark:bg-surface-800 dark:text-surface-300">{recommendation.shippedExcerpt}</pre>
                    </div>
                    <div>
                      <h5 className="text-xs font-semibold uppercase tracking-wide text-surface-500">Generalized diff</h5>
                      <p className="mt-2 text-sm text-surface-600 dark:text-surface-300">{recommendation.rationale}</p>
                      <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-surface-50 p-3 text-xs text-surface-600 dark:bg-surface-800 dark:text-surface-300">{recommendation.generalizedDiff}</pre>
                      <div className="mt-3 text-xs text-surface-400">
                        Source chat {recommendation.sourceChatSessionId} · messages {recommendation.sourceMessageIds.join(', ')}
                      </div>
                      <div className="mt-1 text-xs text-surface-400">
                        Learning Run {recommendation.sourceLearningRunId} · {recommendation.reviewerProvider ?? 'unknown reviewer'}{recommendation.reviewerModel ? ` / ${recommendation.reviewerModel}` : ''}
                      </div>
                      <Button size="sm" variant="outline" className="mt-3" onClick={async () => {
                        const response = await api.learning.getRecommendationSource(recommendation.id);
                        setRecommendationSources((sources) => ({
                          ...sources,
                          [recommendation.id]: response.source,
                        }));
                      }}>Load source evidence</Button>
                      {recommendationSources[recommendation.id] && (
                        <div className="mt-3 space-y-2 rounded-md border border-surface-200 p-3 dark:border-surface-700">
                          {recommendationSources[recommendation.id].evidenceSets.map((evidence) => (
                            <div key={evidence.learningRunId} className="space-y-1">
                              <div className="text-xs font-medium text-surface-700 dark:text-surface-200">
                                {evidence.chatName} · Learning Run {evidence.learningRunId}
                              </div>
                              {evidence.messages.map((message) => (
                                <div key={message.id} className="text-xs text-surface-600 dark:text-surface-300">
                                  <span className="font-medium">{message.role}:</span>{' '}
                                  <span className="whitespace-pre-wrap">{message.content}</span>
                                  {message.truncated ? ' [truncated]' : ''}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 space-y-3 border-t border-surface-200 pt-4 dark:border-surface-700">
                    <textarea
                      value={recommendationNotes[recommendation.id] ?? recommendation.supportNote ?? ''}
                      onChange={(event) => setRecommendationNotes((notes) => ({ ...notes, [recommendation.id]: event.target.value }))}
                      maxLength={2000}
                      placeholder="Support note (local to this instance)"
                      className="min-h-20 w-full rounded-md border border-surface-300 bg-white p-3 text-sm dark:border-surface-600 dark:bg-surface-800"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={recommendationStatuses[recommendation.id] ?? recommendation.status}
                        onChange={(event) => setRecommendationStatuses((statuses) => ({
                          ...statuses,
                          [recommendation.id]: event.target.value as ShippedSkillRecommendationStatus,
                        }))}
                        className="rounded-md border border-surface-300 bg-white px-3 py-2 text-sm dark:border-surface-600 dark:bg-surface-800"
                        aria-label={`Status for ${recommendation.skillName}`}
                      >
                        <option value="new">New</option>
                        <option value="watching">Watch</option>
                        <option value="accepted_for_dev">Accept for development</option>
                        <option value="dismissed_tenant_specific">Dismiss tenant-specific</option>
                        <option value="dismissed_incorrect">Dismiss incorrect</option>
                        <option value="resolved_by_release">Mark resolved by release</option>
                      </select>
                      <Button size="sm" onClick={() => setPendingRecommendationAction({
                        recommendation,
                        status: recommendationStatuses[recommendation.id] ?? recommendation.status,
                      })}>Apply status</Button>
                    </div>
                    {recommendationView.events.some((event) => event.recommendationId === recommendation.id) && (
                      <details className="rounded-md bg-surface-50 p-3 dark:bg-surface-800">
                        <summary className="cursor-pointer text-xs font-medium text-surface-700 dark:text-surface-200">Local status history</summary>
                        <div className="mt-2 space-y-2">
                          {recommendationView.events
                            .filter((event) => event.recommendationId === recommendation.id)
                            .slice(0, 20)
                            .map((event) => (
                              <div key={event.id} className="text-xs text-surface-500 dark:text-surface-400">
                                {event.summary}{event.actorUsername ? ` · ${event.actorUsername}` : ''} · {new Date(event.createdAt).toLocaleString()}
                              </div>
                            ))}
                        </div>
                      </details>
                    )}
                  </div>
                </details>
              ))}
            {recommendationView.recommendations.length === 0 && (
              <div className="rounded-lg border border-dashed border-surface-300 px-4 py-10 text-center text-sm text-surface-500 dark:border-surface-700 dark:text-surface-400">No shipped-Skill recommendations.</div>
            )}
          </div>
        )
      ) : tab === 'skills' ? (
        loading && !skillView ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary-500" /></div>
        ) : !skillView || skillView.skills.length === 0 ? (
          <div className="rounded-lg border border-dashed border-surface-300 px-4 py-10 text-center text-sm text-surface-500 dark:border-surface-700 dark:text-surface-400">
            No active tenant-learned Skills are visible.
          </div>
        ) : (
          <div className="space-y-3">
            {skillView.skills.map((skill) => (
              <div key={skill.id} className="rounded-lg border border-surface-200 p-4 dark:border-surface-700">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-medium text-surface-900 dark:text-surface-100">{skill.skillName}</h4>
                      <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">tenant learned</span>
                      <span className="rounded-full bg-surface-100 px-2 py-0.5 text-xs text-surface-600 dark:bg-surface-700 dark:text-surface-300">{skill.mode}</span>
                      <span className={cn(
                        'rounded-full px-2 py-0.5 text-xs',
                        skill.status === 'review_due'
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                          : skill.status === 'active'
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                            : 'bg-surface-100 text-surface-600 dark:bg-surface-700 dark:text-surface-300'
                      )}>{skill.status.replace('_', ' ')}</span>
                      {skill.pinned && <Pin className="h-3.5 w-3.5 text-primary-500" />}
                      {skill.baseState === 'removed' && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Base Skill removed</span>
                      )}
                    </div>
                    {skill.baseShippedSkillName && (
                      <p className="mt-1 text-xs text-surface-400">
                        Overlay on shipped Skill {skill.baseShippedSkillName}
                        {skill.activeRevision?.baseVersionObserved ? ` ${skill.activeRevision.baseVersionObserved}` : ''}
                      </p>
                    )}
                    {skill.activeRevision && (
                      <div className="mt-3 whitespace-pre-wrap rounded-md bg-surface-50 p-3 text-sm text-surface-600 dark:bg-surface-800 dark:text-surface-300">
                        {skill.activeRevision.markdownContent}
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-surface-400">
                      <span>Revision {skill.activeRevision?.version ?? '—'} of {skill.revisionCount}</span>
                      <span>{skill.health.explicitInvocationCount} explicit invocation{skill.health.explicitInvocationCount === 1 ? '' : 's'}</span>
                      <span>{skill.health.materializationCount} materialization{skill.health.materializationCount === 1 ? '' : 's'}</span>
                      {skill.activeRevision?.reviewerProvider && <span>{skill.activeRevision.reviewerProvider}</span>}
                      {skill.activeRevision?.createdAt && <span>{new Date(skill.activeRevision.createdAt).toLocaleString()}</span>}
                    </div>
                  </div>
                  {skillView.permissions.manageTenantSkills && (
                    <div className="flex flex-wrap gap-2">
                      {skill.status === 'review_due' && skill.baseState !== 'removed' && (
                        <Button size="sm" variant="outline" onClick={async () => {
                          await api.learning.acknowledgeTenantSkillReview(skill.id);
                          toast.success('Tenant Skill review acknowledged with a new revision');
                          await load();
                        }}>Mark reviewed</Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={skill.pinned ? 'Unpin tenant Skill' : 'Pin tenant Skill'}
                        onClick={async () => {
                          await api.learning.setTenantSkillPinned(skill.id, !skill.pinned);
                          await load();
                        }}
                      >
                        {skill.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                      </Button>
                      {skill.status === 'archived' ? (
                        skill.baseState !== 'removed' && (
                          <Button size="sm" variant="outline" onClick={async () => {
                            await api.learning.setTenantSkillLifecycle(skill.id, 'active');
                            await load();
                          }}>Restore</Button>
                        )
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setPendingSkillAction({ kind: 'archive', skill })}>Archive</Button>
                      )}
                    </div>
                  )}
                </div>
                {skillView.permissions.manageTenantSkills && skill.revisions.length > 1 && (
                  <details className="mt-4 border-t border-surface-200 pt-3 dark:border-surface-700">
                    <summary className="cursor-pointer text-sm font-medium text-surface-700 dark:text-surface-200">Revision history</summary>
                    <div className="mt-3 space-y-2">
                      {skill.revisions.map((revision) => (
                        <div key={revision.id} className="flex items-start justify-between gap-3 rounded-md bg-surface-50 p-3 dark:bg-surface-800">
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-surface-700 dark:text-surface-200">Revision {revision.version}{revision.id === skill.activeRevisionId ? ' · active' : ''}</div>
                            <div className="mt-1 line-clamp-2 whitespace-pre-wrap text-xs text-surface-500 dark:text-surface-400">{revision.markdownContent}</div>
                          </div>
                          {revision.id !== skill.activeRevisionId && skill.baseState !== 'removed' && (
                            <Button size="sm" variant="outline" onClick={() => setPendingSkillAction({ kind: 'rollback', skill, revision })}>Rollback</Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        )
      ) : loading && !view ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary-500" /></div>
      ) : memories.length === 0 ? (
        <div className="rounded-lg border border-dashed border-surface-300 px-4 py-10 text-center text-sm text-surface-500 dark:border-surface-700 dark:text-surface-400">
          No Memory Entries are visible in this scope.
        </div>
      ) : (
        <div className="divide-y divide-surface-200 overflow-hidden rounded-lg border border-surface-200 dark:divide-surface-700 dark:border-surface-700">
          {memories.map((memory) => {
            const canManage = memory.scope === 'user'
              || Boolean(view?.permissions.manageTenantMemory);
            return (
              <div key={memory.id} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-medium text-surface-900 dark:text-surface-100">{memory.title}</h4>
                      <span className="rounded-full bg-surface-100 px-2 py-0.5 text-xs text-surface-600 dark:bg-surface-700 dark:text-surface-300">{memory.category}</span>
                      <span className={cn('rounded-full px-2 py-0.5 text-xs', statusClass(memory.status))}>{memory.status}</span>
                      {memory.conflictGroupId && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">conflict</span>}
                      {memory.reviewDue && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">review due</span>}
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-surface-600 dark:text-surface-300">{memory.content}</p>
                    <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-surface-400">
                      <span>{memory.memoryKey}</span>
                      {view?.permissions.viewAllUserMemory && memory.ownerUsername && <span>{memory.ownerUsername}</span>}
                      <span>{memory.sourceProvider || memory.source}</span>
                      <span>{new Date(memory.createdAt).toLocaleString()}</span>
                      {memory.reviewAfter && <span>Review after {new Date(memory.reviewAfter).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex flex-wrap gap-2">
                      {memory.status === 'active' && !memory.conflictGroupId && (
                        <Button size="sm" variant="outline" onClick={() => setMemoryEditor({
                          memory,
                          memoryKey: memory.memoryKey,
                          category: memory.category,
                          title: memory.title,
                          content: memory.content,
                        })}>
                          <Pencil className="mr-2 h-3.5 w-3.5" /> Correct
                        </Button>
                      )}
                      {memory.status === 'archived' && activeByKey.has(memory.memoryKey) && (
                        <Button size="sm" variant="outline" onClick={() => setPendingAction({ kind: 'rollback', memory })}>Rollback</Button>
                      )}
                      {memory.conflictGroupId && (
                        <Button size="sm" variant="outline" onClick={() => setPendingAction({ kind: 'resolve', memory })}>Select revision</Button>
                      )}
                      {memory.status !== 'archived' && (
                        <Button size="sm" variant="outline" onClick={() => setPendingAction({ kind: 'archive', memory })}>Archive</Button>
                      )}
                      {memory.status === 'archived' && !activeByKey.has(memory.memoryKey) && !memory.conflictGroupId && (
                        <Button size="sm" variant="outline" onClick={async () => {
                          await api.learning.setMemoryLifecycle(memory.id, 'active');
                          await load();
                        }}>Restore</Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view && tab !== 'skills' && tab !== 'recommendations' && view.events.length > 0 && (
        <details className="rounded-lg border border-surface-200 p-4 dark:border-surface-700">
          <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium text-surface-800 dark:text-surface-100">
            <History className="h-4 w-4" /> Recent history
          </summary>
          <div className="mt-3 space-y-2">
            {view.events.slice(0, 20).map((event) => (
              <div key={event.id} className="text-xs text-surface-500 dark:text-surface-400">
                {event.summary} · {new Date(event.createdAt).toLocaleString()}
              </div>
            ))}
          </div>
        </details>
      )}

      <ConfirmDialog
        isOpen={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        onConfirm={() => void performPendingAction()}
        title={pendingAction?.kind === 'archive' ? 'Archive Memory Entry?' : pendingAction?.kind === 'rollback' ? 'Roll back to this revision?' : 'Resolve this conflict?'}
        message={pendingAction?.kind === 'archive'
          ? `“${pendingAction.memory.title}” will leave active context but remain in history.`
          : pendingAction?.kind === 'rollback'
            ? `A new revision will copy “${pendingAction.memory.title}” and archive the current revision.`
            : `This revision of “${pendingAction?.memory.title ?? ''}” will become active and the other conflicting revisions will be archived.`}
        confirmLabel={pendingAction?.kind === 'archive' ? 'Archive' : pendingAction?.kind === 'rollback' ? 'Roll back' : 'Select revision'}
        variant="destructive"
        isLoading={saving}
      />
      <ConfirmDialog
        isOpen={Boolean(pendingRecommendationAction)}
        onClose={() => setPendingRecommendationAction(null)}
        onConfirm={() => void performPendingRecommendationAction()}
        title="Update local recommendation status?"
        message={`“${pendingRecommendationAction?.recommendation.skillName ?? ''}” will be marked ${pendingRecommendationAction?.status.replace(/_/g, ' ') ?? ''}. This records a local support decision only; it does not change a shipped Skill or send data elsewhere.`}
        confirmLabel="Apply status"
        variant="destructive"
        isLoading={saving}
      />
      <ConfirmDialog
        isOpen={Boolean(pendingSkillAction)}
        onClose={() => setPendingSkillAction(null)}
        onConfirm={() => void performPendingSkillAction()}
        title={pendingSkillAction?.kind === 'archive' ? 'Archive tenant Skill?' : 'Roll back tenant Skill?'}
        message={pendingSkillAction?.kind === 'archive'
          ? `“${pendingSkillAction.skill.skillName}” will be removed from active provider runtimes but its immutable history will remain available.`
          : `Revision ${pendingSkillAction?.revision?.version ?? ''} of “${pendingSkillAction?.skill.skillName ?? ''}” will become active. Later revisions remain in history.`}
        confirmLabel={pendingSkillAction?.kind === 'archive' ? 'Archive' : 'Roll back'}
        variant="destructive"
        isLoading={saving}
      />
    </div>
  );
}
